import {
  AlertTriangle,
  Clock,
  LogIn,
  LogOut,
  Map as MapIcon,
  Menu,
  MoonStar,
  Sun,
  TrainFront,
  User as UserIcon,
} from 'lucide-react';
import * as React from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { GlobalSearch } from '@/components/feature/GlobalSearch';
import { RecoveryCodeDialog } from '@/components/feature/RecoveryCodeDialog';
import { SaveSheetProvider } from '@/components/feature/SaveSheetTrigger';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useTheme } from '@/design-system/ThemeProvider';
import {
  useMigrateLocalToServerOnSignin,
  usePromotePendingTripOnSignin,
} from '@/hooks/useSavedTrips';
import { useUserAuth } from '@/lib/auth/UserAuthContext';
import { cn } from '@/lib/utils';

interface NavItem {
  to: string;
  label: string;
  Icon: typeof Clock;
}

const PUBLIC_NAV: readonly NavItem[] = [
  { to: '/today', label: 'Today', Icon: Clock },
  { to: '/map', label: 'Map', Icon: MapIcon },
  { to: '/alerts', label: 'Alerts', Icon: AlertTriangle },
  // Explore (/explore) is parked for launch — the lateness metrics need a
  // rethink before it earns a spot. See docs/explore-parked.md.
];

const SIGNED_IN_EXTRA: NavItem = { to: '/you', label: 'You', Icon: UserIcon };

// Routes that render edge-to-edge with no main padding or scrolling —
// the route itself owns the full viewport (e.g. the live map). These
// routes drop their in-page <h1>, so the shell renders the page title
// in the global header instead. Other routes keep their own headings
// to avoid duplicate level-1 landmarks.
const FULL_BLEED_PATHS: ReadonlySet<string> = new Set(['/map']);

const PAGE_TITLES: Readonly<Record<string, string>> = Object.fromEntries(
  [...PUBLIC_NAV, SIGNED_IN_EXTRA]
    .filter(({ to }) => FULL_BLEED_PATHS.has(to))
    .map(({ to, label }) => [to, label]),
);

function NavList({
  onNavigate,
  signedIn,
}: {
  onNavigate?: () => void;
  signedIn: boolean;
}): React.JSX.Element {
  const items = signedIn ? [...PUBLIC_NAV, SIGNED_IN_EXTRA] : PUBLIC_NAV;
  return (
    <nav aria-label="Primary" className="flex flex-col gap-1">
      {items.map(({ to, label, Icon }) => (
        <NavLink
          key={to}
          to={to}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-caption font-medium transition-colors',
              isActive
                ? 'bg-primary/15 text-primary'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )
          }
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
          <span>{label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function UserMenu(): React.JSX.Element {
  const { status, user, signout } = useUserAuth();
  const navigate = useNavigate();

  if (status === 'authenticated' && user) {
    return (
      <div className="flex items-center gap-2">
        <span className="hidden text-caption text-muted-foreground sm:inline" title={user.email}>
          {user.email}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label="Sign out"
          onClick={() => {
            void signout().then(() => {
              navigate('/', { replace: true });
            });
          }}
        >
          <LogOut className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">Sign out</span>
        </Button>
      </div>
    );
  }
  return (
    <Button asChild type="button" variant="ghost" size="sm">
      <Link to="/signin" aria-label="Sign in">
        <LogIn className="h-4 w-4" aria-hidden="true" />
        <span className="hidden sm:inline">Sign in</span>
      </Link>
    </Button>
  );
}

function Brand(): React.JSX.Element {
  return (
    <div className="flex items-center gap-2 px-2 py-3">
      <span
        aria-hidden="true"
        className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground"
      >
        <TrainFront className="h-4 w-4" />
      </span>
      <span className="text-subheading font-semibold tracking-tight">train-tracks</span>
    </div>
  );
}

function ThemeToggle(): React.JSX.Element {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      onClick={toggleTheme}
    >
      {isDark ? <Sun className="h-4 w-4" /> : <MoonStar className="h-4 w-4" />}
    </Button>
  );
}

export function Shell(): React.JSX.Element {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const { status } = useUserAuth();
  const signedIn = status === 'authenticated';
  const { pathname } = useLocation();
  const pageTitle = PAGE_TITLES[pathname];
  const fullBleed = FULL_BLEED_PATHS.has(pathname);
  // Drain any pending-trip / local-leg stash regardless of which page
  // the freshly signed-in user lands on. Both hooks no-op when there's
  // nothing to migrate.
  useMigrateLocalToServerOnSignin();
  usePromotePendingTripOnSignin();

  return (
    <SaveSheetProvider>
      <div className="flex min-h-full flex-col bg-background text-foreground">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-primary-foreground"
        >
          Skip to content
        </a>
        <div className="flex flex-1">
          <aside
            aria-label="Sidebar"
            className="hidden w-60 shrink-0 flex-col border-r border-border bg-surface md:flex"
          >
            <Brand />
            <Separator />
            <div className="flex-1 overflow-y-auto p-3">
              <NavList signedIn={signedIn} />
            </div>
          </aside>

          <div className="flex min-w-0 flex-1 flex-col">
            <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border bg-surface px-4">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="md:hidden"
                    aria-label="Open navigation menu"
                  >
                    <Menu className="h-4 w-4" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-72 p-0">
                  <SheetTitle className="sr-only">Navigation</SheetTitle>
                  <SheetDescription className="sr-only">
                    Primary navigation for train-tracks.
                  </SheetDescription>
                  <Brand />
                  <Separator />
                  <div className="p-3">
                    <NavList
                      signedIn={signedIn}
                      onNavigate={() => {
                        setMobileOpen(false);
                      }}
                    />
                  </div>
                </SheetContent>
              </Sheet>

              <div className="flex min-w-0 flex-1 items-center gap-3">
                {pageTitle ? (
                  <h1 className="shrink-0 truncate text-subheading font-semibold tracking-tight">
                    {pageTitle}
                  </h1>
                ) : null}
                <GlobalSearch />
              </div>
              <UserMenu />
              <ThemeToggle />
            </header>

            <main
              id="main-content"
              className={cn(
                'isolate flex-1',
                fullBleed ? 'overflow-hidden' : 'overflow-y-auto p-6',
              )}
            >
              <Outlet />
            </main>
          </div>
        </div>
        <RecoveryCodeDialog />
      </div>
    </SaveSheetProvider>
  );
}
